/**
 * IncrementalDebugLogger - Debug logging utility for incremental layout operations
 *
 * Extracted from EdgeDocsCanvas.V3.tsx for better modularity and testability.
 *
 * This utility provides conditional logging for incremental layout debugging,
 * controlled by debug settings.
 */

import type { DebugSettings } from './DebugSettings';

/**
 * Subset of debug settings used for incremental logging
 */
export interface IncrementalLogSettings {
  showIncrementalLogs: boolean;
  showDeltaDetails: boolean;
  showSnapshotDiff: boolean;
}

/**
 * Create an incremental debug logger function
 *
 * @param settings - Current debug settings for controlling log visibility
 * @returns Debug logging function with category-based conditional logging
 */
export function createIncrementalDebugLogger(settings: IncrementalLogSettings | DebugSettings) {
  return function incrementalDebugLog(
    category: 'incremental' | 'delta' | 'snapshot' | 'general',
    message: string,
    data?: any
  ) {
    // Always log general messages (for basic debugging)
    if (category === 'general') {
      console.log(`🔍 [Debug] ${message}`, data || '');
      return;
    }

    // Conditional logging based on settings
    if (category === 'incremental' && settings.showIncrementalLogs) {
      console.log(`🔄 [Incremental] ${message}`, data || '');
    }

    if (category === 'delta' && settings.showDeltaDetails) {
      console.log(`📝 [Delta] ${message}`, data || '');
    }

    if (category === 'snapshot' && settings.showSnapshotDiff) {
      console.log(`📸 [Snapshot] ${message}`, data || '');
    }
  };
}
